import type { ReactNode } from 'react'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import { cn } from '@/lib/utils'
import type { QuoteLine } from '../api/types'

/**
 * A quote line's product identity in the dashboard's shared product cell: picture, model,
 * manufacturer and description. Pass `children` for line-specific details under the description.
 */
export function QuoteLineIdentity({
  line,
  title,
  description,
  children,
  compact = false,
  className,
  imageClassName,
}: {
  line: Pick<QuoteLine, 'model' | 'description' | 'manufacturer' | 'primary_asset'>
  /** Overrides the model as the bold title (configuration options show their option name). */
  title?: ReactNode
  description?: string | null
  children?: ReactNode
  compact?: boolean
  className?: string
  imageClassName?: string
}) {
  const model = line.model || line.description
  return (
    <POLineItemProductCell
      className={className}
      compact={compact}
      descriptionContent={
        description || children ? (
          <>
            {description ? <span className="block">{description}</span> : null}
            {children}
          </>
        ) : undefined
      }
      imageClassName={imageClassName}
      imageUrl={line.primary_asset?.url ?? null}
      model={model}
      modelContent={
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <span className={cn('text-sm font-medium', compact ? 'truncate' : 'break-words')}>{title ?? model}</span>
          {line.manufacturer && !title ? (
            <span className="text-xs text-muted-foreground">{line.manufacturer}</span>
          ) : null}
        </span>
      }
    />
  )
}
